import type { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import {
  blendPatch, boxOf, BRUSH_DEFAULT, hasSeeThrough, BRUSH_MAX, BRUSH_MIN, clampRect, eraseRegion, fitView, fixProblem, grow, hexOf, keepSteps, MASK_MAX_SIDE,
  maskCells, stampsBetween, union, zoomAt, type Rect, type View
} from '../editor/fixMath';
import type { History } from '../editor/history';
import { shrinkType, UPLOAD_MAX_BYTES } from '../editor/photo';
import { naturalSize, swapSrc } from '../editor/picture';
import { assets, uploadedAs } from '../editor/storage';
import { api } from '../sync/api';
import { $, $in, setPressed } from './dom';
import { toast } from './toast';

/*
 * ─── แก้รูป ─────────────────────────────────────────────────────────────────────────────────────────────────────────
 * A screen over the whole app (like ครอปรูป) that edits the picture's FILE:
 *   ลบด้วย AI   paint (or box) over a word/thing → only a patch around it goes to the group's VM (LaMa, /api/bg/erase),
 *               which fills it in; only the painted part changes on the picture
 *   แปรงทาสี   a colour brush, with "ดูดสีจากรูป"
 *   ประทับตรา  copy another part of the picture over (set the source point, then paint)
 * Two fingers zoom/move. Undo/redo inside the screen keep only the changed parts (fixMath UNDO_MAX_BYTES).
 * "เสร็จ": the edited file replaces the picture's file (same size: its place, crop, shape and แต่งภาพ stay — choice ก) and
 * the file before is kept as `fixOriginal` (EXTRA_PROPS) for "คืนต้นฉบับ"; one undo step on the page. Every failure says why.
 */

type Pic = fabric.Image & { fixOriginal?: string; _originalElement?: HTMLImageElement };
type Tool = 'ai' | 'paint' | 'clone';
type AiMode = 'brush' | 'rect' | 'unbrush';
interface Step { r: Rect; before: ImageData; after: ImageData }
interface Pt { x: number; y: number }

const errText = (e: unknown) => e instanceof Error && e.message ? e.message : String(e);
const MASK_COLOUR = 'rgba(255,45,110,1)';
export const ICON_FIX = '<svg viewBox="0 0 24 24"><path d="M14.5 4.5l5 5L9 20H4v-5z"/><path d="M12 7l5 5"/><path d="M3 3l2 2M7 2v2M2 7h2"/></svg>';

export function initFixView(ed: Editor, history: History, opts: { canEdit: () => boolean; refresh: () => void }) {
  const { canvas } = ed;
  const root = $('fix'), stage = $('fxStage'), cv = $<HTMLCanvasElement>('fxCanvas'), msg = $('fxMsg');
  const say = (t: string, ok = false) => { msg.textContent = t; msg.hidden = !t; msg.classList.toggle('ok', ok); };

  let pic: Pic | null = null, from = '';
  let W = 0, H = 0, ms = 1;                         // picture px; mask px per picture px
  let work: HTMLCanvasElement | null = null, spare: HTMLCanvasElement | null = null, mask: HTMLCanvasElement | null = null;
  let maskBefore: HTMLCanvasElement | null = null;   // the mask at the start of a stroke (a second finger puts it back)
  let view: View = { s: 1, ox: 0, oy: 0 }, minS = 0.01;
  let fitted = true;   // the view is "พอดีจอ": kept so when the panel below grows or shrinks
  let tool: Tool = 'ai', aiMode: AiMode = 'brush', size = BRUSH_DEFAULT, picking = false, settingSrc = false;
  let cloneSrc: Pt | null = null, cloneOffset: Pt | null = null;
  let steps: Step[] = [], at = 0;                   // undo: steps[0..at) are applied
  let busy = false;
  const g = (c: HTMLCanvasElement | null) => c?.getContext('2d', { willReadFrequently: true }) ?? null;

  /* ─── tools ─── */
  const sizeIn = $in('fxSize');
  sizeIn.min = String(BRUSH_MIN); sizeIn.max = String(BRUSH_MAX);
  sizeIn.oninput = () => { size = Number(sizeIn.value); $('fxSizeV').textContent = `${size}`; paint(); };
  $('fxTools').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b?.dataset.t) return;
    tool = b.dataset.t as Tool; picking = false; settingSrc = false; paintTools(); paint();
  };
  $('fxAiMode').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b?.dataset.m) return;
    aiMode = b.dataset.m as AiMode; paintTools();
  };
  $('fxAiClear').onclick = () => { const m = g(mask); m?.clearRect(0, 0, mask!.width, mask!.height); paint(); };
  $('fxPick').onclick = () => { picking = !picking; paintTools(); };
  $('fxSrc').onclick = () => { settingSrc = !settingSrc; paintTools(); };
  $('fxFit').onclick = () => { fit(); paint(); };
  function paintTools() {
    setPressed('fxTools', b => b.dataset.t === tool);
    setPressed('fxAiMode', b => b.dataset.m === aiMode);
    $('fxAiRow').hidden = tool !== 'ai';
    $('fxPaintRow').hidden = tool !== 'paint';
    $('fxCloneRow').hidden = tool !== 'clone';
    $('fxPick').setAttribute('aria-pressed', String(picking));
    $('fxSrc').setAttribute('aria-pressed', String(settingSrc));
    $('fxCloneInfo').textContent = settingSrc ? 'แตะจุดบนรูปที่จะใช้เป็นต้นแบบ' : cloneSrc
      ? 'ระบายทับจุดที่จะแก้ — ภาพจากจุดต้นแบบ (วงสีเขียว) ตามนิ้วไปด้วย · แตะ "ตั้งจุดต้นแบบ" เพื่อเลือกจุดใหม่'
      : 'แตะ "ตั้งจุดต้นแบบ" แล้วแตะจุดบนรูปที่จะคัดลอกมา จากนั้นระบายทับจุดที่จะแก้';
    ($('fxUndo') as HTMLButtonElement).disabled = at === 0 || busy;
    ($('fxRedo') as HTMLButtonElement).disabled = at === steps.length || busy;
    ($('fxAiGo') as HTMLButtonElement).disabled = busy;
    ($('fxDone') as HTMLButtonElement).disabled = busy;
    $('fxAiGo').textContent = busy ? 'กำลังลบด้วย AI…' : 'ลบด้วย AI';
  }

  /* ─── view ─── */
  function fit() { view = fitView(W, H, stage.clientWidth, stage.clientHeight); minS = Math.min(view.s, 1) / 4; fitted = true; }
  const toPic = (cx: number, cy: number): Pt => {
    const r = stage.getBoundingClientRect();
    return { x: (cx - r.left - view.ox) / view.s, y: (cy - r.top - view.oy) / view.s };
  };
  let hover: Pt | null = null, rectFrom: Pt | null = null, rectTo: Pt | null = null;
  let frame = 0;
  const paint = () => { if (!frame) frame = requestAnimationFrame(draw); };
  function draw() {
    frame = 0;
    if (!work) return;
    const sw = stage.clientWidth, sh = stage.clientHeight, dpr = window.devicePixelRatio || 1;
    if (cv.width !== Math.round(sw * dpr) || cv.height !== Math.round(sh * dpr)) {
      cv.width = Math.round(sw * dpr); cv.height = Math.round(sh * dpr);
      cv.style.width = `${sw}px`; cv.style.height = `${sh}px`;
    }
    const c = cv.getContext('2d');
    if (!c) { say('เครื่องนี้วาดหน้าแก้รูปไม่ได้ (หน่วยความจำไม่พอ) ลองปิดแอปอื่นแล้วลองใหม่'); return; }
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.clearRect(0, 0, sw, sh);
    const x = view.ox, y = view.oy, w = W * view.s, h = H * view.s;
    // see-through parts show as a light chequer
    c.fillStyle = '#d9d6e0'; c.fillRect(x, y, w, h);
    c.fillStyle = '#f4f3f7';
    const q = 12;
    for (let yy = 0; yy < h; yy += q) for (let xx = ((yy / q) % 2) * q; xx < w; xx += 2 * q) c.fillRect(x + xx, y + yy, Math.min(q, w - xx), Math.min(q, h - yy));
    c.imageSmoothingEnabled = view.s < 1;
    c.drawImage(work, x, y, w, h);
    if (mask) { c.globalAlpha = 0.5; c.drawImage(mask, x, y, w, h); c.globalAlpha = 1; }
    if (rectFrom && rectTo) {
      c.strokeStyle = '#fff'; c.setLineDash([6, 4]); c.lineWidth = 1.5;
      c.strokeRect(x + Math.min(rectFrom.x, rectTo.x) * view.s, y + Math.min(rectFrom.y, rectTo.y) * view.s, Math.abs(rectTo.x - rectFrom.x) * view.s, Math.abs(rectTo.y - rectFrom.y) * view.s);
      c.setLineDash([]);
    }
    const ring = (p: Pt, colour: string, r = size / 2) => {
      c.beginPath(); c.arc(x + p.x * view.s, y + p.y * view.s, r, 0, Math.PI * 2);
      c.lineWidth = 2; c.strokeStyle = 'rgba(0,0,0,.6)'; c.stroke();
      c.lineWidth = 1; c.strokeStyle = colour; c.stroke();
    };
    if (tool === 'clone' && cloneSrc) {
      const s = cloneOffset && hover ? { x: hover.x + cloneOffset.x, y: hover.y + cloneOffset.y } : cloneSrc;
      ring(s, '#3ddc84'); ring(s, '#3ddc84', 3);
    }
    if (hover && !(tool === 'ai' && aiMode === 'rect') && !picking && !settingSrc) ring(hover, '#fff');
  }

  /* ─── strokes ─── */
  interface Stroke { last: Pt; dirty: Rect | null; kind: 'mask' | 'paint' | 'clone' | 'rect'; off?: Pt }
  let stroke: Stroke | null = null;
  const pointers = new Map<number, Pt>();
  let pinch: { d: number; s: number; mid: Pt; v: View } | null = null;

  function startStroke(p: Pt) {
    if (!work || !spare || busy) return;
    if (picking) {
      const d = g(work)!.getImageData(Math.max(0, Math.min(W - 1, Math.floor(p.x))), Math.max(0, Math.min(H - 1, Math.floor(p.y))), 1, 1).data;
      $in('fxColor').value = hexOf(d[0], d[1], d[2]);
      picking = false; paintTools();
      say(`ดูดสี ${hexOf(d[0], d[1], d[2])} แล้ว ระบายได้เลย`, true);
      return;
    }
    if (tool === 'clone' && settingSrc) {
      cloneSrc = p; cloneOffset = null; settingSrc = false; paintTools(); paint();
      return;
    }
    if (tool === 'clone' && !cloneSrc) { say('แตะ "ตั้งจุดต้นแบบ" แล้วแตะจุดที่จะคัดลอกมาก่อน'); return; }
    say('');
    if (tool === 'ai') {
      // the mask before this stroke (a second finger = a pinch: the stroke is taken back)
      g(maskBefore)!.clearRect(0, 0, maskBefore!.width, maskBefore!.height);
      g(maskBefore)!.drawImage(mask!, 0, 0);
      if (aiMode === 'rect') { rectFrom = p; rectTo = p; stroke = { last: p, dirty: null, kind: 'rect' }; return; }
      stroke = { last: p, dirty: null, kind: 'mask' };
      maskLine(p, p);
      return;
    }
    // the picture before this stroke: what undo goes back to, and what ประทับตรา copies from (not its own strokes)
    g(spare)!.clearRect(0, 0, W, H);
    g(spare)!.drawImage(work, 0, 0);
    if (tool === 'clone') {
      // aligned (like Photoshop): the distance to the source is set by the first stroke and kept
      if (!cloneOffset) cloneOffset = { x: cloneSrc!.x - p.x, y: cloneSrc!.y - p.y };
      stroke = { last: p, dirty: null, kind: 'clone', off: cloneOffset };
      cloneStamp(p);
      return;
    }
    stroke = { last: p, dirty: null, kind: 'paint' };
    paintLine(p, p);
  }
  const brushPx = () => size / view.s;   // the brush in picture px
  function maskLine(a: Pt, b: Pt) {
    const m = g(mask)!;
    m.save();
    m.globalCompositeOperation = aiMode === 'unbrush' ? 'destination-out' : 'source-over';
    m.strokeStyle = MASK_COLOUR; m.lineCap = 'round'; m.lineJoin = 'round'; m.lineWidth = brushPx() * ms;
    m.beginPath(); m.moveTo(a.x * ms, a.y * ms); m.lineTo(b.x * ms + 0.01, b.y * ms); m.stroke();
    m.restore();
  }
  function grown(a: Pt, b: Pt, r: number): Rect {
    return { x: Math.min(a.x, b.x) - r - 2, y: Math.min(a.y, b.y) - r - 2, w: Math.abs(a.x - b.x) + 2 * r + 4, h: Math.abs(a.y - b.y) + 2 * r + 4 };
  }
  function paintLine(a: Pt, b: Pt) {
    const c = g(work)!;
    c.save();
    c.strokeStyle = $in('fxColor').value; c.lineCap = 'round'; c.lineJoin = 'round'; c.lineWidth = brushPx();
    c.beginPath(); c.moveTo(a.x, a.y); c.lineTo(b.x + 0.01, b.y); c.stroke();
    c.restore();
    stroke!.dirty = union(stroke!.dirty, grown(a, b, brushPx() / 2));
  }
  function cloneStamp(p: Pt) {
    const c = g(work)!, r = brushPx() / 2, off = stroke!.off!;
    c.save();
    c.beginPath(); c.arc(p.x, p.y, r, 0, Math.PI * 2); c.clip();
    // the whole picture as it was at the start of the stroke, moved by the offset: only the circle shows
    c.drawImage(spare!, -off.x, -off.y);
    c.restore();
    stroke!.dirty = union(stroke!.dirty, grown(p, p, r));
  }
  function moveStroke(p: Pt) {
    const s = stroke; if (!s) return;
    if (s.kind === 'rect') { rectTo = p; }
    else if (s.kind === 'mask') maskLine(s.last, p);
    else if (s.kind === 'paint') paintLine(s.last, p);
    else for (const q of stampsBetween(s.last, p, brushPx() / 4)) cloneStamp(q);
    s.last = p;
    paint();
  }
  function endStroke() {
    const s = stroke; stroke = null;
    if (!s || !work) return;
    if (s.kind === 'rect') {
      if (rectFrom && rectTo && Math.abs(rectTo.x - rectFrom.x) > 2 && Math.abs(rectTo.y - rectFrom.y) > 2) {
        const m = g(mask)!;
        m.fillStyle = MASK_COLOUR;
        m.fillRect(Math.min(rectFrom.x, rectTo.x) * ms, Math.min(rectFrom.y, rectTo.y) * ms, Math.abs(rectTo.x - rectFrom.x) * ms, Math.abs(rectTo.y - rectFrom.y) * ms);
      } else say('ลากเป็นกรอบทับบริเวณที่จะลบ (แตะเฉย ๆ ไม่ได้กรอบ)');
      rectFrom = rectTo = null;
      paint();
      return;
    }
    if (s.kind === 'mask') return;
    const r = s.dirty && clampRect(s.dirty, W, H);
    if (r) addStep(r, g(spare)!.getImageData(r.x, r.y, r.w, r.h));
  }
  /** a second finger came down: what the stroke did is taken back (it was the start of a pinch) */
  function dropStroke() {
    const s = stroke; stroke = null;
    if (!s || !work) return;
    if (s.kind === 'rect') { rectFrom = rectTo = null; return; }
    if (s.kind === 'mask') { const m = g(mask)!; m.clearRect(0, 0, mask!.width, mask!.height); m.drawImage(maskBefore!, 0, 0); return; }
    const r = s.dirty && clampRect(s.dirty, W, H);
    if (r) g(work)!.putImageData(g(spare)!.getImageData(r.x, r.y, r.w, r.h), r.x, r.y);
  }

  /** one undo step: the part `r` before (given) and now (read) */
  function addStep(r: Rect, before: ImageData) {
    steps = steps.slice(0, at);
    steps.push({ r, before, after: g(work)!.getImageData(r.x, r.y, r.w, r.h) });
    const keep = keepSteps(steps.map(s => s.before.data.length * 2));
    if (keep < steps.length) steps = steps.slice(steps.length - keep);
    at = steps.length;
    paintTools();
  }
  $('fxUndo').onclick = () => {
    if (!at || busy) return;
    const s = steps[--at]; g(work)!.putImageData(s.before, s.r.x, s.r.y); paintTools(); paint();
  };
  $('fxRedo').onclick = () => {
    if (at >= steps.length || busy) return;
    const s = steps[at++]; g(work)!.putImageData(s.after, s.r.x, s.r.y); paintTools(); paint();
  };

  /* ─── pointers: one = the tool, two = zoom/move ─── */
  stage.addEventListener('pointerdown', e => {
    if (!work) return;
    stage.setPointerCapture(e.pointerId);
    const p = { x: e.clientX, y: e.clientY };
    pointers.set(e.pointerId, p);
    if (pointers.size === 2) {
      dropStroke();
      const [a, b] = [...pointers.values()];
      const r = stage.getBoundingClientRect();
      pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), s: view.s, mid: { x: (a.x + b.x) / 2 - r.left, y: (a.y + b.y) / 2 - r.top }, v: { ...view } };
      paint();
      return;
    }
    if (pointers.size > 2) return;
    hover = toPic(e.clientX, e.clientY);
    startStroke(hover);
    paint();
  });
  stage.addEventListener('pointermove', e => {
    if (!work) return;
    if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch && pointers.size >= 2) {
      const [a, b] = [...pointers.values()];
      const r = stage.getBoundingClientRect();
      const mid = { x: (a.x + b.x) / 2 - r.left, y: (a.y + b.y) / 2 - r.top };
      const v = zoomAt(pinch.v, Math.hypot(a.x - b.x, a.y - b.y) / pinch.d, pinch.mid.x, pinch.mid.y, minS);
      view = { s: v.s, ox: v.ox + mid.x - pinch.mid.x, oy: v.oy + mid.y - pinch.mid.y }; fitted = false;
      paint();
      return;
    }
    hover = toPic(e.clientX, e.clientY);
    if (stroke && pointers.has(e.pointerId)) moveStroke(hover); else paint();
  });
  const up = (e: PointerEvent) => {
    pointers.delete(e.pointerId);
    if (pinch) { if (!pointers.size) pinch = null; return; }
    endStroke();
    if (e.pointerType !== 'mouse') hover = null;
    paint();
  };
  stage.addEventListener('pointerup', up);
  stage.addEventListener('pointercancel', e => { pointers.delete(e.pointerId); dropStroke(); if (!pointers.size) pinch = null; paint(); });
  stage.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse' && !stroke) { hover = null; paint(); } });
  stage.addEventListener('wheel', e => {
    if (!work) return;
    e.preventDefault();
    const r = stage.getBoundingClientRect();
    view = zoomAt(view, Math.pow(0.998, e.deltaY), e.clientX - r.left, e.clientY - r.top, minS); fitted = false;
    paint();
  }, { passive: false });
  new ResizeObserver(() => { if (!work) return; if (fitted) fit(); paint(); }).observe(stage);

  /* ─── ลบด้วย AI ─── */
  $('fxAiGo').onclick = () => void aiErase();
  async function aiErase() {
    if (!work || !mask || busy) return;
    const m = g(mask)!;
    const cells = maskCells(m.getImageData(0, 0, mask.width, mask.height).data);
    const b = boxOf(cells, mask.width, mask.height);
    const reg = eraseRegion(b && { x: b.x / ms, y: b.y / ms, w: b.w / ms, h: b.h / ms }, W, H);
    if (!reg) { say('ยังไม่ได้ระบายบริเวณที่จะลบ — ระบายหรือตีกรอบทับคำ/สิ่งที่จะลบก่อน แล้วกด "ลบด้วย AI"'); return; }
    busy = true; paintTools(); say('กำลังลบด้วย AI… ส่งเฉพาะส่วนที่ระบายไปที่เครื่องของกลุ่ม ใช้เวลาประมาณ 2–30 วินาที', true);
    const forPic = pic;
    try {
      // the patch and its mask, at the patch size (scaled down when the patch is big)
      const patch = document.createElement('canvas'); patch.width = reg.pw; patch.height = reg.ph;
      const pc = patch.getContext('2d')!;
      pc.drawImage(work, reg.x, reg.y, reg.w, reg.h, 0, 0, reg.pw, reg.ph);
      const mk = document.createElement('canvas'); mk.width = reg.pw; mk.height = reg.ph;
      const mc = mk.getContext('2d', { willReadFrequently: true })!;
      mc.drawImage(mask, reg.x * ms, reg.y * ms, reg.w * ms, reg.h * ms, 0, 0, reg.pw, reg.ph);
      // a little wider than painted: the soft edge of letters goes too (else a faint outline stays)
      const on = grow(maskCells(mc.getImageData(0, 0, reg.pw, reg.ph).data), reg.pw, reg.ph, 3);
      const bw = mc.createImageData(reg.pw, reg.ph);
      for (let i = 0; i < on.length; i++) { const v = on[i] ? 255 : 0; bw.data[i * 4] = bw.data[i * 4 + 1] = bw.data[i * 4 + 2] = v; bw.data[i * 4 + 3] = 255; }
      mc.putImageData(bw, 0, 0);
      const toPng = (c: HTMLCanvasElement) => new Promise<Blob>((res, rej) => c.toBlob(x => x ? res(x) : rej(new Error('สร้างไฟล์ส่วนของรูปไม่สำเร็จ (หน่วยความจำไม่พอ)')), 'image/png'));
      const [imgBlob, maskBlob] = await Promise.all([toPng(patch), toPng(mk)]);
      const r = await api.bgErase(imgBlob, maskBlob);
      if (pic !== forPic || !work) { toast('ลบด้วย AI เสร็จแล้ว แต่ปิดหน้าแก้รูปไปก่อน จึงไม่ได้ใส่ผล', true); return; }
      const bmp = await createImageBitmap(r.blob).catch(() => null);
      if (!bmp) throw new Error('เปิดผลลบด้วย AI ไม่ได้');
      if (bmp.width !== reg.pw || bmp.height !== reg.ph) throw new Error(`ผลลบด้วย AI ขนาด ${bmp.width}×${bmp.height} ไม่เท่าส่วนที่ส่งไป ${reg.pw}×${reg.ph}`);
      // only the painted part (grown) changes: the result and the mask scaled back to the picture's px, then each pixel
      // is the original and the result mixed by the mask's (soft-edged) weight — never a see-through ring (BUG-67)
      const keep = document.createElement('canvas'); keep.width = reg.w; keep.height = reg.h;
      const kc = keep.getContext('2d', { willReadFrequently: true })!;
      kc.imageSmoothingQuality = 'high';
      kc.drawImage(bmp, 0, 0, reg.w, reg.h);
      const res = kc.getImageData(0, 0, reg.w, reg.h);
      const alpha = document.createElement('canvas'); alpha.width = reg.pw; alpha.height = reg.ph;
      const ac = alpha.getContext('2d')!, ad = ac.createImageData(reg.pw, reg.ph);
      for (let i = 0; i < on.length; i++) ad.data[i * 4 + 3] = on[i] ? 255 : 0;
      ac.putImageData(ad, 0, 0);
      kc.clearRect(0, 0, reg.w, reg.h);
      kc.drawImage(alpha, 0, 0, reg.w, reg.h);
      const weight = kc.getImageData(0, 0, reg.w, reg.h);
      const wc = g(work)!, before = wc.getImageData(reg.x, reg.y, reg.w, reg.h);
      const mixed = wc.createImageData(reg.w, reg.h);
      mixed.data.set(blendPatch(before.data, res.data, weight.data));
      wc.putImageData(mixed, reg.x, reg.y);
      bmp.close();
      addStep({ x: reg.x, y: reg.y, w: reg.w, h: reg.h }, before);
      m.clearRect(0, 0, mask.width, mask.height);
      say(`ลบด้วย AI แล้ว (${(r.ms / 1000).toFixed(1)} วินาที · วันนี้ใช้ ${r.used}/${r.limit} ครั้ง) ไม่ถูกใจกด เลิกทำ ↶ · กด "เสร็จ" เพื่อใช้กับรูป`, true);
    } catch (e) {
      say(`ลบด้วย AI ไม่สำเร็จ: ${errText(e)} (ส่วนที่ระบายยังอยู่ ลองใหม่ได้ หรือใช้แปรงทาสี/ประทับตราแทน)`);
    } finally {
      busy = false; paintTools(); paint();
    }
  }

  /* ─── open / close ─── */
  function release() {
    for (const c of [work, spare, mask, maskBefore]) if (c) { c.width = 0; c.height = 0; }   // Safari frees the memory now
    work = spare = mask = maskBefore = null;
    steps = []; at = 0; pic = null; stroke = null; pointers.clear(); pinch = null;
    cloneSrc = cloneOffset = null; picking = settingSrc = false;
  }
  function close() { root.hidden = true; release(); }

  /** why the open work cannot be changed now (read-only, a page loading), or null */
  const blocked = () => !opts.canEdit() ? 'งานนี้เปิดดูอย่างเดียว แก้รูปไม่ได้' : history.loading ? 'กำลังเปิดหน้าอยู่ รอสักครู่แล้วลองใหม่' : null;

  function open() {
    const o = canvas.getActiveObject() as Pic | null;
    if (!o || o.type !== 'image') { toast('เลือกรูปก่อน แล้วกด "แก้รูป"', true); return; }
    const why = blocked(); if (why) { toast(why, true); return; }
    const nat = naturalSize(o);
    const bad = fixProblem(nat.w, nat.h); if (bad) { toast(bad, true); return; }
    const el = o._originalElement ?? (o.getElement() as HTMLImageElement);
    release();
    W = nat.w; H = nat.h;
    ms = Math.min(1, MASK_MAX_SIDE / Math.max(W, H));
    const make = (w: number, h: number) => { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; };
    try {
      work = make(W, H); spare = make(W, H);
      mask = make(Math.max(1, Math.round(W * ms)), Math.max(1, Math.round(H * ms)));
      maskBefore = make(mask.width, mask.height);
      const c = g(work);
      if (!c || !g(spare) || !g(mask)) throw new Error('หน่วยความจำไม่พอ');
      c.drawImage(el, 0, 0, W, H);   // the file itself: แต่งภาพ (filters) and ครอป stay on the picture, untouched
      c.getImageData(0, 0, 1, 1);    // a picture from a site that does not allow reading its pixels fails here
    } catch (e) {
      release();
      toast(e instanceof DOMException && e.name === 'SecurityError'
        ? 'อ่านพิกเซลของรูปนี้ไม่ได้ (รูปมาจากเว็บอื่นที่ไม่อนุญาต) จึงแก้รูปไม่ได้'
        : `เปิดหน้าแก้รูปไม่ได้: ${errText(e)} ลองปิดแอปอื่นแล้วลองใหม่`, true);
      return;
    }
    pic = o; from = o.getSrc();
    size = BRUSH_DEFAULT; sizeIn.value = String(size); $('fxSizeV').textContent = `${size}`;
    tool = 'ai'; aiMode = 'brush';
    say('');
    root.hidden = false;
    fit(); paintTools(); paint();
  }

  $('fxCancel').onclick = () => {
    if (busy) { say('รอให้ลบด้วย AI เสร็จก่อน'); return; }
    if (at > 0 && !confirm('ทิ้งสิ่งที่แก้ในรูปนี้ทั้งหมด?')) return;
    close();
  };

  $('fxDone').onclick = () => void done();
  async function done() {
    if (!pic || !work || busy) return;
    if (at === 0) { close(); toast('ไม่ได้แก้อะไรในรูป รูปยังเหมือนเดิม'); return; }
    const m = g(mask)!;
    if (boxOf(maskCells(m.getImageData(0, 0, mask!.width, mask!.height).data), mask!.width, mask!.height)
      && !confirm('ยังมีส่วนที่ระบายไว้แต่ยังไม่ได้กด "ลบด้วย AI" — ส่วนนั้นจะไม่ถูกลบ ใช้รูปตามที่เห็นเลยไหม?')) return;
    const why = blocked(); if (why) { say(why); return; }
    const target = pic;
    // the autosave may have moved this same file to the server while the screen was open (blob: → server): still the
    // picture that was read, not another file (BUG-35)
    const now = target.getSrc(), sentFrom = now !== from && uploadedAs.get(from) === now ? now : from;
    busy = true; paintTools(); say('กำลังใช้กับรูป…', true);
    try {
      const own = assets.get(sentFrom) ?? (sentFrom.startsWith('blob:') ? await fetch(sentFrom).then(r => r.blob()).catch(() => undefined) : undefined);
      // a JPG has no see-through pixels: a picture that has some (a PNG edited, or anything left part see-through) is
      // saved as PNG, never flattened to dark pixels (BUG-67)
      const jpg = shrinkType(own?.type, sentFrom) === 'image/jpeg';
      const type = jpg && hasSeeThrough(g(work)!.getImageData(0, 0, W, H).data) ? 'image/png' : shrinkType(own?.type, sentFrom);
      const blob = await new Promise<Blob | null>(res => work!.toBlob(res, type, 0.92));
      if (!blob) throw new Error('สร้างไฟล์รูปที่แก้แล้วไม่สำเร็จ (หน่วยความจำไม่พอ) ลองปิดแอปอื่นแล้วลองใหม่');
      if (blob.size > UPLOAD_MAX_BYTES) throw new Error(`ไฟล์รูปที่แก้แล้วใหญ่ ${(blob.size / 1024 / 1024).toFixed(1)} MB เกินที่เซิร์ฟเวอร์รับ ${UPLOAD_MAX_BYTES / 1024 / 1024} MB`);
      // the file before, for "คืนต้นฉบับ": a picture not uploaded yet is uploaded now (an address on this device only
      // would be gone after the app is closed)
      let original = target.fixOriginal || sentFrom, note = '';
      if (!target.fixOriginal && sentFrom.startsWith('blob:')) {
        try { original = (await api.upload(own ?? await fetch(sentFrom).then(r => r.blob()))).url; }
        catch (e) { original = ''; note = ` · เก็บรูปเดิมไว้ไม่ได้ (${errText(e)}) จึงกด "คืนต้นฉบับ" ไม่ได้ ใช้เลิกทำแทน`; }
      }
      const url = URL.createObjectURL(blob);
      let swapped: boolean;
      try { swapped = await swapSrc(target, url, 'เปิดรูปที่แก้แล้วไม่ได้', 1, sentFrom, true); }
      catch (e) { URL.revokeObjectURL(url); throw e; }
      if (!swapped) { URL.revokeObjectURL(url); throw new Error('รูปนี้ถูกเปลี่ยนไฟล์ระหว่างแก้ (เช่น บันทึกขึ้นเซิร์ฟเวอร์หรือแต่งภาพย่อรูป) กดยกเลิกแล้วเปิดแก้รูปใหม่'); }
      assets.set(url, blob);
      if (original) target.set('fixOriginal' as keyof fabric.Object, original);
      canvas.requestRenderAll();
      history.push();
      close();
      opts.refresh();
      toast(`ใช้รูปที่แก้แล้ว${original ? ' · กด "คืนต้นฉบับ" ได้ทุกเมื่อ' : ''}${note}`, !!note);
    } catch (e) {
      busy = false; paintTools();
      say(`ใช้กับรูปไม่สำเร็จ: ${errText(e)} (สิ่งที่แก้ยังอยู่ในหน้านี้)`);
    } finally { busy = false; if (!root.hidden) paintTools(); }
  }

  /* ─── คืนต้นฉบับ ─── */
  async function restore(img: Pic) {
    const orig = img.fixOriginal;
    if (!orig) return;
    const why = blocked(); if (why) { toast(why, true); return; }
    try {
      const swapped = await swapSrc(img, orig, 'เปิดรูปต้นฉบับ (ก่อนแก้รูป) ไม่ได้', 1, img.getSrc(), true);
      if (!swapped) { toast('รูปนี้ถูกเปลี่ยนไฟล์ระหว่างนั้น ลองกด "คืนต้นฉบับ" อีกครั้ง', true); return; }
      img.set('fixOriginal' as keyof fabric.Object, undefined);
      canvas.requestRenderAll();
      history.push();
      opts.refresh();
      toast('คืนรูปต้นฉบับแล้ว (ก่อนแก้รูป) · เลิกทำได้');
    } catch (e) { toast(`คืนต้นฉบับไม่สำเร็จ: ${errText(e)}`, true); }
  }

  /** the toolbar chips for a picture: แก้รูป, and คืนต้นฉบับ once it was edited */
  function chips(o: Pic): Array<{ act: () => void; label: string; html: string }> {
    const list = [{ act: () => open(), label: 'แก้รูป (ลบด้วย AI, แปรงทาสี, ประทับตรา)', html: `${ICON_FIX}<span>แก้รูป</span>` }];
    if (o.fixOriginal) list.push({ act: () => void restore(o), label: 'คืนต้นฉบับ (ก่อนแก้รูป)', html: `${ICON_FIX}<span>คืนต้นฉบับ</span>` });
    return list;
  }

  return { open, chips, restore, get isOpen() { return !root.hidden; } };
}
